import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import PersonAddAlt1Rounded from '@mui/icons-material/PersonAddAlt1Rounded';
import PersonRemoveRounded from '@mui/icons-material/PersonRemoveRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { ApiError } from '@/api';
import { CsvImportDialog } from '@/components/data/CsvImportDialog';
import { PeoplePicker, type PersonOption } from '@/components/data/PeoplePicker';
import { EmptyState } from '@/components/EmptyState';
import { useConfirm } from '@/components/feedback/confirmContext';
import { useNotify } from '@/components/feedback/notificationsContext';
import { UserAvatar } from '@/components/UserAvatar';
import { useAsync } from '@/hooks/useAsync';
import {
  addGroupMembers,
  createGroup,
  deleteGroup,
  getGroup,
  listGroups,
  removeGroupMember,
  updateGroup,
  type GroupInput,
} from '@/platform/api';
import { ROLE_LABEL, type Group } from '@/platform/types';
import { PageHeader } from '../PageHeader';
import { PeopleTabs } from './PeopleTabs';

export function GroupsPage() {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);
  const groups = useAsync((signal) => listGroups(showArchived, signal), [showArchived]);
  const [editing, setEditing] = useState<Group | 'new' | null>(null);
  const [importing, setImporting] = useState(false);

  return (
    <>
      <PageHeader
        title="Turmas"
        subtitle="Agrupe estudantes para atribuir atividades de uma vez."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<UploadFileRounded />}
              onClick={() => setImporting(true)}
            >
              Importar planilha
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setEditing('new')}
            >
              Nova turma
            </Button>
          </>
        }
      />
      <PeopleTabs />
      <FormControlLabel
        control={
          <Switch checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
        }
        label="Mostrar arquivadas"
        sx={{ mb: 2 }}
      />
      {groups.error ? (
        <Alert severity="error">Não foi possível carregar as turmas.</Alert>
      ) : groups.loading ? (
        <Grid container spacing={2}>
          {[0, 1, 2].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <Skeleton variant="rounded" height={130} />
            </Grid>
          ))}
        </Grid>
      ) : (groups.data ?? []).length === 0 ? (
        <Card>
          <EmptyState
            icon={GroupsRounded}
            title="Nenhuma turma"
            description="Crie turmas como “Ciência da Computação – 8º semestre” e adicione os estudantes."
          />
        </Card>
      ) : (
        <Grid container spacing={2}>
          {(groups.data ?? []).map((g) => (
            <Grid key={g.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                sx={{
                  height: '100%',
                  opacity: g.archived ? 0.6 : 1,
                  '&:hover': { borderColor: 'primary.light' },
                }}
              >
                <CardActionArea
                  onClick={() => void navigate(`/people/groups/${g.id}`)}
                  sx={{ height: '100%' }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {g.name}
                      </Typography>
                      {g.archived && <Chip size="small" label="Arquivada" />}
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      sx={{ flexWrap: 'wrap', mb: 1.5 }}
                    >
                      {g.code && (
                        <Chip size="small" variant="soft" color="primary" label={g.code} />
                      )}
                      {g.term && <Chip size="small" variant="soft" label={g.term} />}
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ alignItems: 'center', color: 'text.secondary' }}
                    >
                      <GroupsRounded sx={{ fontSize: 18 }} />
                      <Typography variant="body2">
                        {g.memberCount} {g.memberCount === 1 ? 'pessoa' : 'pessoas'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <GroupDialog
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={(g) => {
          setEditing(null);
          groups.reload();
          if (g) void navigate(`/people/groups/${g.id}`);
        }}
      />
      <CsvImportDialog
        open={importing}
        onClose={() => setImporting(false)}
        title="Importar turmas"
        importPath="/groups/import"
        templatePath="/groups/import/template"
        templateName="modelo-turmas.csv"
        onImported={() => groups.reload()}
        previewColumns={[
          { key: 'name', label: 'Nome' },
          { key: 'code', label: 'Código' },
          { key: 'term', label: 'Período' },
        ]}
        hints={
          <Typography variant="body2">
            Colunas: <b>nome</b>, <b>codigo</b>, <b>periodo</b> e <b>descricao</b>. Para colocar
            estudantes nas turmas, importe as pessoas com a coluna <b>turmas</b>.
          </Typography>
        }
      />
    </>
  );
}

function GroupDialog({
  target,
  onClose,
  onSaved,
}: {
  target: Group | 'new' | null;
  onClose: () => void;
  onSaved: (created: Group | null) => void;
}) {
  const notify = useNotify();
  const existing = target && target !== 'new' ? target : null;
  const [form, setForm] = useState<GroupInput>({
    name: '',
    code: null,
    term: null,
    description: null,
    archived: false,
  });
  const [last, setLast] = useState<typeof target>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (target !== last) {
    setLast(target);
    setError(null);
    setForm(
      existing
        ? {
            name: existing.name,
            code: existing.code,
            term: existing.term,
            description: existing.description,
            archived: existing.archived,
          }
        : { name: '', code: null, term: null, description: null, archived: false },
    );
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) return setError('Dê um nome à turma.');
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        code: form.code?.trim() || null,
        term: form.term?.trim() || null,
      };
      if (existing) {
        await updateGroup(existing.id, payload);
        notify('Turma atualizada.', 'success');
        onSaved(null);
      } else {
        const created = await createGroup(payload);
        notify('Turma criada. Agora adicione as pessoas.', 'success');
        onSaved(created);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={Boolean(target)}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { component: 'form', onSubmit: submit, noValidate: true } as object }}
    >
      <DialogTitle>{existing ? 'Editar turma' : 'Nova turma'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
            placeholder="Ciência da Computação – 8º semestre"
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Código"
              value={form.code ?? ''}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="CC-2026-2"
              fullWidth
            />
            <TextField
              label="Período"
              value={form.term ?? ''}
              onChange={(e) => setForm({ ...form, term: e.target.value })}
              placeholder="2026/2"
              fullWidth
            />
          </Stack>
          <TextField
            label="Descrição"
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value || null })}
            multiline
            minRows={2}
          />
          {existing && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.archived}
                  onChange={(e) => setForm({ ...form, archived: e.target.checked })}
                />
              }
              label="Arquivada (some das listas, mantém o histórico)"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" disabled={saving} sx={{ minWidth: 96 }}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function GroupDetailPage() {
  const { groupId = '' } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const confirm = useConfirm();
  const group = useAsync((signal) => getGroup(groupId, signal), [groupId]);
  const [adding, setAdding] = useState<PersonOption[]>([]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const g = group.data;

  const add = async () => {
    setBusy(true);
    try {
      await addGroupMembers(
        groupId,
        adding.map((p) => p.id),
      );
      notify(`${adding.length} pessoa(s) adicionada(s).`, 'success');
      setAdding([]);
      group.reload();
    } finally {
      setBusy(false);
    }
  };

  if (group.error) return <Alert severity="error">Turma não encontrada.</Alert>;
  if (!g) return <Skeleton variant="rounded" height={300} />;

  return (
    <>
      <PageHeader
        overline={
          <Link
            component={RouterLink}
            to="/people/groups"
            variant="body2"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}
          >
            <ArrowBackRounded sx={{ fontSize: 16 }} /> Turmas
          </Link>
        }
        title={g.name}
        subtitle={[g.code, g.term, `${g.memberCount} pessoas`].filter(Boolean).join(' · ')}
        actions={
          <>
            <Button variant="outlined" startIcon={<EditRounded />} onClick={() => setEditing(true)}>
              Editar
            </Button>
            <Tooltip title="Excluir turma">
              <IconButton
                aria-label="Excluir turma"
                onClick={async () => {
                  if (
                    await confirm({
                      title: 'Excluir turma?',
                      description:
                        'As pessoas não são apagadas, só saem da turma. Atividades atribuídas a ela deixam de aparecer para esses estudantes.',
                      confirmLabel: 'Excluir',
                      danger: true,
                    })
                  ) {
                    await deleteGroup(g.id);
                    notify('Turma excluída.', 'success');
                    void navigate('/people/groups');
                  }
                }}
              >
                <DeleteOutlineRounded />
              </IconButton>
            </Tooltip>
          </>
        }
      />
      {g.description && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {g.description}
        </Typography>
      )}
      <Card sx={{ mb: 2.5 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Adicionar pessoas
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Box sx={{ flex: 1 }}>
              <PeoplePicker value={adding} onChange={setAdding} label="Buscar pessoas" />
            </Box>
            <Button
              variant="contained"
              startIcon={<PersonAddAlt1Rounded />}
              disabled={!adding.length || busy}
              onClick={() => void add()}
              sx={{ alignSelf: { sm: 'center' } }}
            >
              Adicionar
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <Card>
        {g.members.length === 0 ? (
          <EmptyState
            compact
            icon={GroupsRounded}
            title="Turma vazia"
            description="Busque pessoas acima ou importe por CSV (coluna turmas)."
          />
        ) : (
          g.members.map((m) => (
            <Stack
              key={m.id}
              direction="row"
              spacing={1.5}
              sx={(t) => ({
                px: 2.5,
                py: 1.25,
                alignItems: 'center',
                '& + &': { borderTop: `1px solid ${t.vars.palette.divider}` },
              })}
            >
              <UserAvatar user={{ ...m, signInMethod: 'unknown' }} sx={{ width: 34, height: 34 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {m.name ?? m.email}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {m.email}
                  {m.registration ? ` · ${m.registration}` : ''}
                </Typography>
              </Box>
              {m.role !== 'student' && (
                <Chip size="small" variant="soft" label={ROLE_LABEL[m.role]} />
              )}
              <Tooltip title="Remover da turma">
                <IconButton
                  aria-label={`Remover ${m.name ?? m.email} da turma`}
                  onClick={async () => {
                    await removeGroupMember(g.id, m.id);
                    group.reload();
                  }}
                >
                  <PersonRemoveRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))
        )}
      </Card>
      <GroupDialog
        target={editing ? g : null}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          group.reload();
        }}
      />
    </>
  );
}
